(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let decoderPromise;

  const loadDecoder = (source) => {
    if (window.ZXingBrowser?.BrowserMultiFormatOneDReader) return Promise.resolve();
    if (decoderPromise) return decoderPromise;

    decoderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = source;
      script.async = true;
      script.onload = () =>
        window.ZXingBrowser?.BrowserMultiFormatOneDReader
          ? resolve()
          : reject(new Error('Barcode decoder did not initialize'));
      script.onerror = () => reject(new Error('Barcode decoder could not be loaded'));
      document.head.appendChild(script);
    }).catch((error) => {
      decoderPromise = undefined;
      throw error;
    });

    return decoderPromise;
  };

  const isValidRetailBarcode = (value) => {
    if (!/^\d{8}$|^\d{12,14}$/.test(value)) return false;

    const digits = [...value].map(Number);
    const checkDigit = digits.pop();
    const sum = digits
      .reverse()
      .reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);

    return (10 - (sum % 10)) % 10 === checkDigit;
  };

  const initializeScanners = () => {
    document.querySelectorAll('details-modal.header__search').forEach((searchModal) => {
      if (searchModal.dataset.barcodeInitialized === 'true') return;

      const openButton = searchModal.querySelector('[data-barcode-scan-open]');
      const closeButton = searchModal.querySelector('[data-barcode-scan-close]');
      const scanner = searchModal.querySelector('[data-barcode-scanner]');
      const status = searchModal.querySelector('[data-barcode-status]');
      const video = searchModal.querySelector('[data-barcode-video]');
      const searchInput = searchModal.querySelector('input[type="search"]');
      if (!openButton || !closeButton || !scanner || !status || !video || !searchInput) return;

      searchModal.dataset.barcodeInitialized = 'true';
      let controls;
      let opening = false;
      let session = 0;

      const stopCamera = () => {
        controls?.stop?.();
        controls = undefined;
        window.ZXingBrowser?.BrowserCodeReader?.releaseAllStreams?.();

        if (video.srcObject) {
          video.srcObject.getTracks().forEach((track) => track.stop());
          video.srcObject = null;
        }
      };

      const closeScanner = () => {
        session += 1;
        opening = false;
        stopCamera();
        scanner.hidden = true;
        document.body.classList.remove('barcode-scanner-open');
      };

      const useBarcode = (barcode) => {
        searchInput.value = barcode;
        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        searchInput.dispatchEvent(new Event('change', { bubbles: true }));
        navigator.vibrate?.(80);
        closeScanner();
        searchInput.focus({ preventScroll: true });
      };

      const openScanner = async () => {
        if (!mobileQuery.matches || opening || controls) return;

        opening = true;
        const currentSession = ++session;
        searchInput.blur();
        scanner.hidden = false;
        document.body.classList.add('barcode-scanner-open');
        status.textContent = 'Opening camera…';

        try {
          await loadDecoder(scanner.dataset.barcodeDecoderSrc);
          if (currentSession !== session) return;

          const reader = new window.ZXingBrowser.BrowserMultiFormatOneDReader(undefined, {
            delayBetweenScanAttempts: 80,
            delayBetweenScanSuccess: 500,
          });

          controls = await reader.decodeFromConstraints(
            {
              audio: false,
              video: {
                facingMode: { ideal: 'environment' },
                width: { ideal: 1920 },
                height: { ideal: 1080 },
              },
            },
            video,
            (result) => {
              const barcode = result?.getText?.()?.replace(/\D/g, '');
              if (!barcode || !isValidRetailBarcode(barcode)) return;
              status.textContent = `Barcode found: ${barcode}`;
              useBarcode(barcode);
            }
          );

          if (currentSession !== session) {
            controls?.stop?.();
            controls = undefined;
            return;
          }

          opening = false;
          status.textContent = 'Hold the barcode inside the frame';
        } catch (error) {
          if (currentSession !== session) return;
          console.error('Live barcode scan failed:', error);
          opening = false;
          stopCamera();
          status.textContent = 'The camera could not be opened. Check Safari camera permission and try again.';
        }
      };

      openButton.addEventListener('click', openScanner);
      closeButton.addEventListener('click', closeScanner);
      searchModal.querySelector('details')?.addEventListener('toggle', (event) => {
        if (!event.currentTarget.open) closeScanner();
      });
      window.addEventListener('pagehide', closeScanner);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeScanners);
  } else {
    initializeScanners();
  }
  document.addEventListener('shopify:section:load', initializeScanners);
})();
